import React, { useEffect } from 'react';
import { useNavigationState } from '../../../hooks/useNavigationState';
import BottomTabBar, { BottomTabItem, TabIcons } from '../BottomTabBar/BottomTabBar';
import Sidebar, { SidebarItem } from '../Sidebar/Sidebar';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { NAVIGATION, TITLES } from '@buzunarul/shared-constants';

/**
 * 🔄 HYBRID NAVIGATION COMPONENT
 * 
 * Componenta inteligentă care decide între BottomTabBar (mobile) și Sidebar (desktop)
 * bazat pe breakpoint-uri responsive. Implementează best practices 2025 pentru
 * navigation adaptiv și progressive enhancement.
 * 
 * Features:
 * - Responsive switching la 768px breakpoint
 * - Integration completă cu useNavigationState
 * - Smooth transitions între layouts
 * - Accessibility support pentru ambele variante
 * - Performance optimization cu lazy rendering
 * 
 * 🔧 LOOP INFINIT FIX (2025-01-15):
 * Problem: useEffect cu `actions` în dependency array cauzau re-render infinit
 * Solution: Eliminat `actions` din deps și adăugat null-check în useEffect
 */

/**
 * 🎨 Iconuri moderne și expresive pentru sidebar
 */
const SidebarIcons = {
  Daily: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Calendar cu indicator pentru ziua curentă */}
      <rect x="2" y="4" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M2 8h16" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6 2v4M14 2v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="10" cy="13" r="2.5" fill="currentColor" opacity="0.2"/>
      <circle cx="10" cy="13" r="1.5" fill="currentColor"/>
    </svg>
  ),
  Transactions: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Receipt/Factură stilizată */}
      <path d="M4 2h12v14l-2 2-2-2-2 2-2-2-2 2-2-2V2z" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      <path d="M7 6h6M7 9h6M7 12h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  LunarGrid: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Calendar grid modern */}
      <rect x="2" y="2" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M2 7h16M7 2v16M13 2v16" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="8" y="8" width="4" height="4" fill="currentColor" opacity="0.2"/>
      <rect x="3" y="13" width="3" height="3" fill="currentColor" opacity="0.4"/>
      <rect x="14" y="3" width="3" height="3" fill="currentColor" opacity="0.4"/>
    </svg>
  ),
  Profile: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* User profile icon */}
      <circle cx="10" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2"/>
      <path d="M3 18c0-3.866 3.134-7 7-7s7 3.134 7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  Settings: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Gear simplificat */}
      <circle cx="10" cy="10" r="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.2"/>
      <path d="M10 3V5M10 15V17M17 10H15M5 10H3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M15.5 5.5L14 7M6 13L4.5 14.5M15.5 14.5L14 13M6 7L4.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  // REMOVED: ScrollbarTest icon - no longer needed
  /*
  ScrollbarTest: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="2" width="11" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" fill="currentColor" fillOpacity="0.1"/>
      <path d="M6 5h5M6 8h5M6 11h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
      <rect x="15" y="5" width="2" height="10" rx="1" fill="currentColor" opacity="0.2"/>
      <rect x="15.25" y="7" width="1.5" height="4" rx="0.75" fill="currentColor" opacity="0.7"/>
      <path d="M16 5.5L16 4.5M16 14.5L16 15.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.5"/>
    </svg>
  )
  */
};

/**
 * Tipul pentru configurația de navigare
 */
interface NavigationConfigItem {
  id: string;
  label: string;
  href: string;
  icon: React.ReactNode;
  testId: string;
  hideOnMobile?: boolean; // Opțional - ascunde pe mobil (nu pe tabletă)
}

/**
 * Configurația de navigare comună pentru ambele componente
 */
const NAVIGATION_CONFIG: NavigationConfigItem[] = [
  {
    id: 'daily',
    label: 'Situația Zilei',
    href: '/daily',
    icon: SidebarIcons.Daily(),
    testId: 'daily-situation-tab'
  },
  {
    id: 'transactions',
    label: 'Tranzacții',
    href: '/transactions',
    icon: SidebarIcons.Transactions(),
    testId: 'transactions-tab'
  },
  {
    id: 'lunar-grid',
    label: 'Grid Lunar',
    href: '/lunar-grid',
    icon: SidebarIcons.LunarGrid(),
    testId: 'lunar-grid-tab',
    hideOnMobile: true // Ascunde doar pe mobil (nu pe tabletă)
  },
  {
    id: 'profile',
    label: 'Profil',
    href: '/profile',
    icon: SidebarIcons.Profile(),
    testId: 'profile-tab'
  },
  {
    id: 'options',
    label: 'Opțiuni',
    href: '/options',
    icon: SidebarIcons.Settings(),
    testId: 'options-tab'
  },
  // REMOVED - Test page no longer needed
  /*
  {
    id: 'scrollbar-test',
    label: '🎯 Test Scrollbar',
    href: '/scrollbar-test',
    icon: SidebarIcons.ScrollbarTest(),
    testId: 'scrollbar-test-tab'
  }
  */
];

/**
 * Props pentru HybridNavigation
 */
export interface HybridNavigationProps {
  /** CSS classes adiționale */
  className?: string;
  /** Data test ID pentru testare */
  testId?: string;
  /** Callback pentru logout (optional) */
  onLogout?: () => void;
  /** Afișează butonul de logout */
  showLogout?: boolean;
}

/**
 * 🎯 HybridNavigation - Componentă inteligentă pentru navigare responsivă
 * 
 * Componenta inteligentă care decide între BottomTabBar (mobile) și Sidebar (desktop)
 * bazat pe media queries și integrează cu useNavigationState pentru state management.
 * 
 * **Mobile (≤767px)**: BottomTabBar fix la bottom
 * **Desktop (≥768px)**: Sidebar collapsible la stânga
 * 
 * **Features:**
 * - Responsive switching automat
 * - Iconuri consistente între mobile și desktop
 * - Integration cu useNavigationState
 * - Support pentru logout button
 * - Accessibility și keyboard navigation
 */
const HybridNavigation = ({
  className,
  testId = 'hybrid-navigation',
  showLogout = true,
  onLogout,
}: HybridNavigationProps) => {
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const { state, actions } = useNavigationState() || {};
  
  // 🔧 FIX: Counter pentru a forța re-render controlat
  const [renderKey, setRenderKey] = React.useState(0);
  const renderKeyTimeoutRef = React.useRef<NodeJS.Timeout | undefined>(undefined);
  
  // 🔧 FIX: Forțează re-render doar la schimbarea state-ului sidebar cu debounce
  React.useEffect(() => {
    // Clear timeout anterior
    if (renderKeyTimeoutRef.current) {
      clearTimeout(renderKeyTimeoutRef.current);
    }
    
    // Setează un nou timeout pentru a evita re-renders rapide
    renderKeyTimeoutRef.current = setTimeout(() => {
      setRenderKey(prev => prev + 1);
    }, 50); // 50ms debounce
    
    return () => {
      if (renderKeyTimeoutRef.current) {
        clearTimeout(renderKeyTimeoutRef.current);
      }
    };
  }, [state?.sidebarExpanded]);

  // Prepare tabs pentru BottomTabBar (filtrează elementele ascunse pe mobil)
  const bottomTabs: BottomTabItem[] = NAVIGATION_CONFIG
    .filter(item => !item.hideOnMobile) // Exclude elementele marcate ca hideOnMobile
    .map(item => ({
      id: item.id,
      label: item.label,
      href: item.href,
      icon: item.icon,
      testId: item.testId
    }));

  // Render pentru mobile - BottomTabBar
  if (isMobile) {
    return (
      <div className="duration-300 animate-in fade-in">
        <BottomTabBar
          tabs={bottomTabs}
          className={className}
          testId={`${testId}-mobile`}
        />
      </div>
    );
  }

  // Render pentru desktop - Sidebar cu iconuri
  return (
    <div className="duration-300 animate-in fade-in">
      <Sidebar
        variant="desktop"
        className={className}
        testId={`${testId}-desktop`}
        key={`sidebar-${renderKey}`} // 🔧 FIX: Key controlat pentru re-render smooth
      >
        {/* Navigation items cu iconuri și tooltips */}
        {NAVIGATION_CONFIG.map((item) => (
          <SidebarItem
            key={item.id} // 🔧 FIX: Key static pentru a preveni recrearea
            to={item.href}
            icon={item.icon}
            testId={item.testId}
            tooltip={item.label}
            aria-label={`Navigate to ${item.label}`}
          >
            {item.label}
          </SidebarItem>
        ))}
        
        {/* Logout button dacă este activat */}
        {showLogout && onLogout && (
          <>
            {/* Separator */}
            <div className="my-4 border-t border-carbon-200 dark:border-carbon-700" />
            
            <SidebarItem
              key="logout"
              onClick={onLogout}
              icon={
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 20 20" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  {/* Door frame */}
                  <path 
                    d="M7 2H4C2.89543 2 2 2.89543 2 4V16C2 17.1046 2.89543 18 4 18H7" 
                    stroke="currentColor" 
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  {/* Exit arrow */}
                  <path 
                    d="M13 14L17 10L13 6" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <path 
                    d="M17 10H8" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round"
                  />
                  {/* Door detail */}
                  <circle cx="6" cy="10" r="0.5" fill="currentColor"/>
                </svg>
              }
              testId="nav-logout"
              variant="ghost"
              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
              tooltip="Logout"
              aria-label="Logout from application"
            >
              Logout
            </SidebarItem>
          </>
        )}
      </Sidebar>
    </div>
  );
};

export default HybridNavigation; 